<script setup>
import { defineAsyncComponent } from 'vue'

const props = defineProps({
  additionalLanguages: {
    type: Array,
    default: () => []
  },
  siteHandle: {
    type: [String],
    default: ''
  }
})

// Dynamically load the language list component
const LocaleSelectorList = defineAsyncComponent(() => 
  import('./list.vue')
)

const filteredLanguages = computed(() => {
  if (!Array.isArray(props.additionalLanguages)) {
    return []
  }

  return props.additionalLanguages.filter(
    (language) => language?.siteHandle !== props.siteHandle
  )
})

const hasLanguages = computed(() => filteredLanguages.value.length > 0)

const currentLanguageLabel = computed(() => {
  if (props.siteHandle === 'delightAi_ko_KR') {
    return 'KO'
  }

  if (props.siteHandle === 'delightAi_ja_JP') {
    return 'JA'
  }

  return 'EN'
})

const isOpen = ref(false)
const rootRef = ref(null)

const toggleMenu = () => {
  if (!hasLanguages.value) return
  isOpen.value = !isOpen.value
}

const closeMenu = () => {
  isOpen.value = false
}

const handleClickOutside = (event) => {
  if (!isOpen.value) return
  const root = rootRef.value
  const target = event.target

  if (!root || !(target instanceof Node)) return

  if (!root.contains(target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <div v-if="hasLanguages"
      ref="rootRef"
      class="locale-selector"
      :class="{ 'is-open': isOpen }">
    <button
      type="button"
      class="toggle"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="listbox"
      @click="toggleMenu"
    >
      <svg class="toggle__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 25"
        fill="none">
        <path d="M12.8923 2.34595C7.37234 2.34595 2.90234 6.82595 2.90234 12.3459C2.90234 17.8659 7.37234 22.3459 12.8923 22.3459C18.4223 22.3459 22.9023 17.8659 22.9023 12.3459C22.9023 6.82595 18.4223 2.34595 12.8923 2.34595ZM19.8223 8.34595H16.8723C16.5523 7.09595 16.0923 5.89595 15.4923 4.78595C17.3323 5.41595 18.8623 6.69595 19.8223 8.34595ZM12.9023 4.38595C13.7323 5.58595 14.3823 6.91595 14.8123 8.34595H10.9923C11.4223 6.91595 12.0723 5.58595 12.9023 4.38595ZM5.16234 14.3459C5.00234 13.7059 4.90234 13.0359 4.90234 12.3459C4.90234 11.6559 5.00234 10.9859 5.16234 10.3459H8.54234C8.46234 11.0059 8.40234 11.6659 8.40234 12.3459C8.40234 13.0259 8.46234 13.6859 8.54234 14.3459H5.16234ZM5.98234 16.3459H8.93234C9.25234 17.5959 9.71234 18.7959 10.3123 19.9059C8.47234 19.2759 6.94234 18.0059 5.98234 16.3459ZM8.93234 8.34595H5.98234C6.94234 6.68595 8.47234 5.41595 10.3123 4.78595C9.71234 5.89595 9.25234 7.09595 8.93234 8.34595ZM12.9023 20.3059C12.0723 19.1059 11.4223 17.7759 10.9923 16.3459H14.8123C14.3823 17.7759 13.7323 19.1059 12.9023 20.3059ZM15.2423 14.3459H10.5623C10.4723 13.6859 10.4023 13.0259 10.4023 12.3459C10.4023 11.6659 10.4723 10.9959 10.5623 10.3459H15.2423C15.3323 10.9959 15.4023 11.6659 15.4023 12.3459C15.4023 13.0259 15.3323 13.6859 15.2423 14.3459ZM15.4923 19.9059C16.0923 18.7959 16.5523 17.5959 16.8723 16.3459H19.8223C18.8623 17.9959 17.3323 19.2759 15.4923 19.9059ZM17.2623 14.3459C17.3423 13.6859 17.4023 13.0259 17.4023 12.3459C17.4023 11.6659 17.3423 11.0059 17.2623 10.3459H20.6423C20.8023 10.9859 20.9023 11.6559 20.9023 12.3459C20.9023 13.0359 20.8023 13.7059 20.6423 14.3459H17.2623Z"
          fill="currentColor"></path>
      </svg>
      <span class="toggle__label">
        {{ currentLanguageLabel }}
      </span>
      <span class="toggle__caret" aria-hidden="true">
        <svg height="12" width="12" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
          <path d="m512 176-64-64-192 192-192-192-64 64 255.999 256z"></path>
        </svg>
      </span>
    </button>

    <transition name="fade">
      <LocaleSelectorList 
          v-if="isOpen"
          :languages="filteredLanguages"
      />
    </transition>
  </div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>
/* Only critical styles that are needed for initial render */
/* List styles moved to dynamically loaded component: list.vue */

.locale-selector {
  position: relative;
  display: flex;
  align-items: center;
}

.toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.toggle__icon {
  flex-shrink: 0;
}

.toggle__label {
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.4;
  text-transform: uppercase;
  margin-inline: 4px;
}

.toggle__caret {
  display: inline-flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: scale(0.7);
}

.is-open .toggle__caret {
  transform: scale(0.7) rotate(180deg);
}

</style>
